/**
 * Completion status utilities for counter orders
 * Checks if an order meets all completion criteria
 */

/**
 * Check if an order meets all completion criteria
 * @param {Object} order - Order object from list view (may have limited fields)
 * @param {Object} fullOrderDetails - Full order details from getdetailsByID (optional)
 * @param {Array} allPatientOrders - All orders for the same patient (optional, for shared signature/ID)
 * @param {Object} allPatientFullDetailsMap - Map of orderId -> full details for all patient orders (optional)
 * @param {Object} bagScanStatus - Bag scan status for this order { isScanned: boolean, isVerified: boolean } (optional)
 * @returns {Object} Completion status object with checks and overall status
 */
export const checkOrderCompletion = (
  order,
  fullOrderDetails = null,
  allPatientOrders = [],
  allPatientFullDetailsMap = {},
  bagScanStatus = null
) => {
  if (!order) {
    return {
      bagScanned: false,
      paymentVerified: false,
      consultationDone: false,
      idCaptured: false,
      signatureDone: false,
      isFullyComplete: false,
      completionCount: 0,
      totalChecks: 5,
    };
  }

  const data = fullOrderDetails || order;
  const boxDetails = (fullOrderDetails && fullOrderDetails.boxDetails) || (order && order.boxDetails) || {};
  const isPosEnabled = boxDetails.is_pos === true;
  const isConsultEnabled = boxDetails.is_consult === true;
  const isDlCaptureEnabled = boxDetails.is_dl_capture === true;

  const checks = {
    bagScanned: false,
    paymentVerified: false,
    consultationDone: false,
    idCaptured: false,
    signatureDone: false,
  };

  const locationCode = data.location_code || order.location_code;
  const hasLocationCode = Boolean(
    locationCode &&
    typeof locationCode === "string" &&
    locationCode.trim() !== "" &&
    locationCode !== "null" &&
    locationCode !== "undefined"
  );

  if (!hasLocationCode) {
    checks.bagScanned = true;
  } else if (bagScanStatus) {
    checks.bagScanned = Boolean(
      bagScanStatus.isScanned === true && bagScanStatus.isVerified === true
    );
  } else {
    checks.bagScanned = false;
  }

  if (!isPosEnabled) {
    checks.paymentVerified = true;
  } else {
    const orderPrice = Number(data.price || order.price || 0);
    if (orderPrice > 0) {
      checks.paymentVerified = Boolean(
        data.is_payroll_deduct === true ||
        order.is_payroll_deduct === true ||
        (data.amount && data.amount.isPaid === true) ||
        (data.is_paid === true) ||
        (order.is_paid === true)
      );
    } else {
      checks.paymentVerified = true;
    }
  }

  if (!isConsultEnabled) {
    checks.consultationDone = true;
  } else {
    const items = (fullOrderDetails && fullOrderDetails.items) || (data.items) || (order.items) || [];
    const hasItemRequiringConsultation = Array.isArray(items) && items.some(
      (item) => item && item.is_consultrequire === true
    );

    if (!hasItemRequiringConsultation) {
      checks.consultationDone = true;
    } else if (fullOrderDetails && fullOrderDetails.consultationDetails) {
      const consultationDetails = fullOrderDetails.consultationDetails;
      if (Array.isArray(consultationDetails) && consultationDetails.length > 0) {
        checks.consultationDone = consultationDetails.some((consultation) => {
          if (!consultation) return false;
          const status =
            (consultation.Status && String(consultation.Status).trim()) ||
            (consultation.status && String(consultation.status).trim()) ||
            "";
          return status.toLowerCase() === "completed";
        });
      } else {
        checks.consultationDone = false;
      }
    } else {
      checks.consultationDone = false;
    }
  }

  if (!isDlCaptureEnabled) {
    checks.idCaptured = true;
  } else {
    const items = (fullOrderDetails && fullOrderDetails.items) || (data.items) || (order.items) || [];
    const hasItemRequiringIdCapture = Array.isArray(items) && items.some(
      (item) => item && item.is_controlledsubstances === true
    );

    if (!hasItemRequiringIdCapture) {
      checks.idCaptured = true;
    } else {
      let hasIdCaptured = false;
      if (fullOrderDetails) {
        const hasScannedId =
          fullOrderDetails.idData &&
          fullOrderDetails.idData.dlrawdata &&
          typeof fullOrderDetails.idData.dlrawdata === "string" &&
          fullOrderDetails.idData.dlrawdata.trim() !== "" &&
          fullOrderDetails.idData.dlrawdata !== "null" &&
          fullOrderDetails.idData.dlrawdata !== "undefined";

        const hasCapturedImages =
          fullOrderDetails.capturedImages &&
          Array.isArray(fullOrderDetails.capturedImages) &&
          fullOrderDetails.capturedImages.some(
            (item) => item && item.image_url && item.image_url.trim() !== ""
          );

        hasIdCaptured = Boolean(hasScannedId || hasCapturedImages);
      }

      if (!hasIdCaptured && allPatientOrders && allPatientOrders.length > 0) {
        hasIdCaptured = allPatientOrders.some((patientOrder) => {
          if (!patientOrder || !patientOrder.id) return false;
          if (patientOrder.id === order.id) return false;
          
          const patientFullDetails = allPatientFullDetailsMap[patientOrder.id] || null;
          
          if (patientFullDetails) {
            const hasScannedId =
              patientFullDetails.idData &&
              patientFullDetails.idData.dlrawdata &&
              typeof patientFullDetails.idData.dlrawdata === "string" &&
              patientFullDetails.idData.dlrawdata.trim() !== "" &&
              patientFullDetails.idData.dlrawdata !== "null" &&
              patientFullDetails.idData.dlrawdata !== "undefined";

            const hasCapturedImages =
              patientFullDetails.capturedImages &&
              Array.isArray(patientFullDetails.capturedImages) &&
              patientFullDetails.capturedImages.some(
                (item) => item && item.image_url && item.image_url.trim() !== ""
              );

            return Boolean(hasScannedId || hasCapturedImages);
          }
          
          return false;
        });
      }

      checks.idCaptured = hasIdCaptured;
    }
  }

  const currentSignatureUrl = data.signature_url || order.signature_url;
  let hasSignature = Boolean(
    currentSignatureUrl &&
    typeof currentSignatureUrl === "string" &&
    currentSignatureUrl.trim() !== "" &&
    currentSignatureUrl !== "null" &&
    currentSignatureUrl !== "undefined"
  );

  if (!hasSignature && allPatientOrders && allPatientOrders.length > 0) {
    hasSignature = allPatientOrders.some((patientOrder) => {
      if (!patientOrder || !patientOrder.id) return false;
      if (patientOrder.id === order.id) return false;
      
      const patientFullDetails = allPatientFullDetailsMap[patientOrder.id] || null;
      const patientSignatureUrl = 
        (patientFullDetails && patientFullDetails.signature_url) || 
        patientOrder.signature_url;
      
      return Boolean(
        patientSignatureUrl &&
        typeof patientSignatureUrl === "string" &&
        patientSignatureUrl.trim() !== "" &&
        patientSignatureUrl !== "null" &&
        patientSignatureUrl !== "undefined"
      );
    });
  }

  checks.signatureDone = hasSignature;

  const allChecks = Object.values(checks);
  const isFullyComplete = allChecks.every((check) => check === true);
  const completionCount = allChecks.filter((check) => check === true).length;

  return {
    ...checks,
    isFullyComplete,
    completionCount,
    totalChecks: allChecks.length,
  };
};




// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/counter/helpers/completionUtils.js